import Card from '@/components/common/Card'
import FormField from '@/components/common/FormField'
import MainHeading from '@/components/common/MainHeading'
import Layout from '@/components/layout/Layout'
import { Button } from '@/components/ui/button'
import type { ILeaveSetting, ILeaveSettingResponse } from '@/interface/leave'
import { LeaveService } from '@/services/Leave/LeaveSetting'
import { showToastNotification } from '@/utility/helper'
import { RoutesLink } from '@/utility/RouteLinks'
import { errorTypes } from '@/utility/validation'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { ThrowError } from '@/api/callApi'

const AddLeaveSetting: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams()
    const [isLoading, setIsLoading] = useState(false);
    const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<any>();

    const getLeaveSettingById = async () => {
        if (!id) return;
        try {
            const response = await LeaveService.getLeaveSettingById(id);
            if (response?.status && response.data) {
                const data = response.data;
                setValue("type", data.type);
                setValue("applicableFrom", data.applicableFrom ? data.applicableFrom.split('T')[0] : "");
                setValue("totalLeavePerMonth", data.totalLeavePerMonth);
                setValue("totalLeavePerYear", data.totalLeavePerYear);
                setValue("yearCarryForward", String(data.yearCarryForward));
                setValue("monthlyCarryForward", String(data.monthlyCarryForward));
            }
        } catch (error) {
            ThrowError(error);
        }
    }

    useEffect(() => {
        if (id) {
            getLeaveSettingById();
        } else {
            // Default initial form values for clean creation
            setValue("yearCarryForward", "false");
            setValue("monthlyCarryForward", "false");
            setValue("isActive", "true");
        }
    }, [id]);

    const onSubmit = async (data: any) => {
        try {
            setIsLoading(true);
            const payload: ILeaveSetting = {
                type: data.type,
                applicableFrom: data.applicableFrom,
                totalLeavePerMonth: data.totalLeavePerMonth,
                totalLeavePerYear: data.totalLeavePerYear,
                yearCarryForward: data.yearCarryForward === "true",
                monthlyCarryForward: data.monthlyCarryForward === "true",
            }

            let response: ILeaveSettingResponse;
            if (id) {
                response = await LeaveService.updateLeaveSetting(payload, id);
            } else {
                response = await LeaveService.createLeaveSetting(payload);
            }

            if (response?.status) {
                showToastNotification("success", response?.message)
                navigate(RoutesLink.leaveSetting)
            } else {
                showToastNotification("danger", response?.message)
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Layout>
            <div className='space-y-6'>
                <MainHeading
                    title={id ? "Edit Leave Setting" : "Add Leave Setting"}
                    isBackButtonVisible
                    onClick={() => navigate(-1)}
                />
                <Card className='overflow-visible'>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                                {...register("type", { ...errorTypes.required('Leave Type') })}
                                type="text"
                                label="Leave Type"
                                placeholder="Enter Leave Type (e.g. Earned Leave)"
                                error={errors.type?.message as string}
                            />
                            <FormField
                                {...register("applicableFrom", { ...errorTypes.required('Applicable From') })}
                                type="date"
                                label="Applicable From"
                                error={errors.applicableFrom?.message as string}
                            />
                            <FormField
                                {...register("totalLeavePerMonth", {
                                    ...errorTypes.required('Total Leave Per Month'),
                                    pattern: {
                                        value: /^[0-9]+(\.[0-9]+)?$/,
                                        message: "Please enter a valid number"
                                    }
                                })}
                                type="text"
                                label="Total Leave Per Month"
                                placeholder="e.g. 1.5"
                                error={errors.totalLeavePerMonth?.message as string}
                            />
                            <FormField
                                {...register("totalLeavePerYear", {
                                    ...errorTypes.required('Total Leave Per Year'),
                                    pattern: {
                                        value: /^[0-9]+(\.[0-9]+)?$/,
                                        message: "Please enter a valid number"
                                    }
                                })}
                                type="text"
                                label="Total Leave Per Year"
                                placeholder="e.g. 18"
                                error={errors.totalLeavePerYear?.message as string}
                            />
                            <FormField
                                {...register("monthlyCarryForward", { ...errorTypes.required("Monthly Carry Forward") })}
                                type="select"
                                label="Monthly Carry Forward"
                                value={watch("monthlyCarryForward")}
                                options={[
                                    { value: 'true', label: 'Yes' },
                                    { value: 'false', label: 'No' }
                                ]}
                                error={errors.monthlyCarryForward?.message as string}
                            />
                            <FormField
                                {...register("yearCarryForward", { ...errorTypes.required("Yearly Carry Forward") })}
                                type="select"
                                label="Yearly Carry Forward"
                                value={watch("yearCarryForward")}
                                options={[
                                    { value: 'true', label: 'Yes' },
                                    { value: 'false', label: 'No' }
                                ]}
                                error={errors.yearCarryForward?.message as string}
                            />
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate(-1)}
                                disabled={isLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? "Saving..." : (id ? "Update Leave Setting" : "Add Leave Setting")}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </Layout>
    )
}

export default AddLeaveSetting